import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
//import { examSetupApi, setupError } from "../../Exams/examSetupApi";

const outlinedLabelProps = {
  shrink: true,
  sx: {
    backgroundColor: "#fff",
    color: "#344767",
    fontSize: "0.95rem",
    fontWeight: 500,
    px: 0.75,
    "&.Mui-focused": { color: "#1976d2" },
  },
};
function examSetupApi() {
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  if (!user?.client_id || !user?.college_id)
    throw new Error("Select a college before managing exam setup");
  const base = `${process.env.REACT_APP_API_BASE_URL}/api/exam-setup/${user.client_id}/${user.college_id}`;
  const token = sessionStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return {
    get: async (path, params) => (await axios.get(`${base}${path}`, { params, headers })).data.data,
    post: async (path, data) => (await axios.post(`${base}${path}`, data, { headers })).data.data,
    put: async (path, data) => (await axios.put(`${base}${path}`, data, { headers })).data.data,
    delete: async (path) => (await axios.delete(`${base}${path}`, { headers })).data,
  };
}

function setupError(error) {
  return error.response?.data?.message || error.message || "Unable to save exam setup";
}

export default function AcademicYears() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [academicYear, setAcademicYear] = useState("");
  const [active, setActive] = useState(true);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const api = examSetupApi();
  const load = async () => {
    try {
      setRows(await api.get("/academic-years"));
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  useEffect(() => {
    load();
  }, []);
  const reset = () => {
    setEditing(null);
    setAcademicYear("");
    setActive(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing)
        await api.put(`/academic-years/${editing}`, {
          academic_year: academicYear,
          is_active: active,
        });
      else await api.post("/academic-years", { academic_year: academicYear, is_active: active });
      setMessage({ type: "success", text: "Academic year saved" });
      reset();
      await load();
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    } finally {
      setBusy(false);
    }
  };
  const edit = (row) => {
    setEditing(row.acyear_id);
    setAcademicYear(row.academic_year);
    setActive(row.is_active === 1);
  };
  const toggle = async (row) => {
    try {
      await api.put(`/academic-years/${row.acyear_id}`, {
        academic_year: row.academic_year,
        is_active: row.is_active !== 1,
      });
      await load();
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  const columns = [
    { field: "college_id", headerName: "College ID", minWidth: 120, flex: 0.7 },
    { field: "academic_year", headerName: "Academic Year", minWidth: 180, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.7,
      valueGetter: (params) => (params.row.is_active === 1 ? "Active" : "Inactive"),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 240,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => edit(params.row)}>
            Edit
          </Button>
          <Button size="small" onClick={() => toggle(params.row)}>
            {params.row.is_active === 1 ? "Deactivate" : "Activate"}
          </Button>
        </Stack>
      ),
    },
  ];
  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Academic Year Management
      </Typography>
      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="College ID"
            InputLabelProps={outlinedLabelProps}
            value={JSON.parse(sessionStorage.getItem("user") || "null")?.college_id || ""}
            disabled
            size="small"
          />
          <TextField
            label="Academic Year"
            placeholder="2026-27"
            InputLabelProps={outlinedLabelProps}
            value={academicYear}
            onChange={(event) => setAcademicYear(event.target.value)}
            size="small"
            required
          />
          <FormControlLabel
            control={
              <Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />
            }
            label="Active"
          />
          <Button
            variant="contained"
            onClick={save}
            disabled={busy || !academicYear.trim()}
            sx={{
              color: "#fff !important",
              "&:hover": { color: "#fff !important" },
              "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26) !important" },
            }}
          >
            {editing ? "Update" : "Add"}
          </Button>
          {editing && <Button onClick={reset}>Cancel</Button>}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.acyear_id}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
}
